import { useEffect, useState, useCallback, useRef } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import MobileNav from './components/MobileNav';
import DownloadAppModal from './components/DownloadAppModal';
import OfferPopup from './components/OfferPopup';
import { scanForReveals } from './scrollReveal';
import Home from './pages/Home';
import Accounts from './pages/Accounts';
import Cards from './pages/Cards';
import ScallopPay from './pages/ScallopPay';
import ScallopPlus from './pages/ScallopPlus';
import Ambassador from './pages/Ambassador';
import Helpdesk from './pages/Helpdesk';
import Support from './pages/Support';
import EnIn from './pages/EnIn';
import OldHome from './pages/OldHome';
import CookiePolicy from './pages/CookiePolicy';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import RefundPolicy from './pages/RefundPolicy';
import MarketingTermsAndConditions from './pages/MarketingTermsAndConditions';

// The exact link the QR code itself should point to (tracking param
// included). Every existing "download the app" link across every page
// still points at the plain https://onelink.to/wch597 href — that's what
// the global click-interceptor below matches on — this is only the URL
// baked into the QR image/its own "open directly" link inside the modal.
const QR_TARGET_LINK = 'https://onelink.to/wch597?olbm=1';
const QR_IMAGE_SRC = '/framerusercontent.com/qr.png';
// Direct store links offered as explicit iOS / Android options inside the
// download modal (desktop only — phones keep the QR-first layout).
const IOS_STORE_LINK = 'https://apps.apple.com/in/app/scallop/id6746711366';
const ANDROID_STORE_LINK =
  'https://play.google.com/store/apps/details?id=com.app.scallop&hl=en_IN';

// Offer campaign popup content:
//  - public/offer-banner-desktop.jpg → 2048x1152 (16:9)
//  - public/offer-banner-mobile.jpg  → 1080x1350 (4:5)
// Whole banner links to /accounts. Shows at most once per session, only
// on home (/) and /accounts — direct visits to either get it, but a
// dismiss/click on one never re-triggers on the other.
const OFFER_ROUTES = ['/', '/accounts'];
const OFFER_CONFIG = {
  desktopSrc: '/offer-banner-desktop.jpg',
  mobileSrc: '/offer-banner-mobile.jpg',
  alt: 'Scallop Business limited-time offer: 50% off swap, deposit and withdrawal fees for your first 3 months',
  ctaLink: '/accounts',
  delayMs: 1500,
  storageKey: 'scallop-offer-dismissed-v4',
};

function App() {
  const location = useLocation();
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [offerOpen, setOfferOpen] = useState(false);

  // React Router swaps pages client-side without a full reload, which
  // means the browser keeps whatever scroll position the previous page
  // was at — clicking a link partway down Home and landing partway down
  // Accounts/ScallopPay instead of at the top. Reset to the top on every
  // route change so each page always opens from its actual start.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // React Router swaps pages without a full reload, so re-scan for new
  // `.reveal` elements every time the route (and therefore the mounted
  // page's content) changes. A short delay lets the new page's DOM
  // actually paint first.
  useEffect(() => {
    const timeout = setTimeout(() => scanForReveals(), 50);
    return () => clearTimeout(timeout);
  }, [location.pathname]);

  // Global click-interceptor. Two kinds of trigger, both caught here
  // instead of navigating away:
  //  1. Every existing "download the app" link across every page (Cards,
  //     Accounts, ScallopPay, Home, EnIn, OldHome) that points at this
  //     exact href.
  //  2. Home.js's "Get Started" buttons — the original Framer site
  //     already marked these data-framer-name="QR Overlay Trigger" to
  //     open exactly this kind of popup; that trigger JS was stripped
  //     during the static conversion (same pattern as other frozen
  //     interactive elements on this site). Two of the three responsive
  //     variants of this button don't even have an href, so matching on
  //     the data-framer-name catches all of them regardless.
  // Any new element added later with either marker is caught automatically.
  useEffect(() => {
    const onDocumentClick = (e) => {
      const link = e.target.closest(
        'a[href="https://onelink.to/wch597"], [data-framer-name="QR Overlay Trigger"]'
      );
      if (!link) return;
      e.preventDefault();
      setQrModalOpen(true);
    };
    document.addEventListener('click', onDocumentClick, true);
    return () => document.removeEventListener('click', onDocumentClick, true);
  }, []);

  const closeQrModal = useCallback(() => setQrModalOpen(false), []);

  // Offer campaign: show at most once per session, only on home and
  // /accounts. Dismissal (X, overlay, ESC, or banner click) persists in
  // sessionStorage so client-side route changes never re-trigger it, while
  // a fresh direct visit to either page (new tab, campaign link) still gets it.
  // shownRef is set only when the popup actually opens (inside the timer),
  // which keeps this StrictMode-safe in local dev (double-effect would
  // otherwise arm-then-clear the timer and never re-arm).
  // QA override: /?offer=1 or /accounts?offer=1 force-opens the popup,
  // bypassing the stored dismissal — reload the URL to re-verify anytime.
  const shownOfferRef = useRef(false);
  const closeOffer = useCallback(() => {
    setOfferOpen(false);
    try {
      sessionStorage.setItem(OFFER_CONFIG.storageKey, '1');
    } catch {
      // storage unavailable (private mode) — popup just shows again next visit
    }
  }, []);

  useEffect(() => {
    let forced = false;
    try {
      forced =
        new URLSearchParams(window.location.search).get('offer') === '1';
    } catch {
      forced = false;
    }
    if (!forced && !OFFER_ROUTES.includes(location.pathname)) return;
    if (shownOfferRef.current || offerOpen) return;
    let dismissed = false;
    try {
      dismissed = !!sessionStorage.getItem(OFFER_CONFIG.storageKey);
    } catch {
      dismissed = false;
    }
    if (dismissed && !forced) return;
    const timeout = setTimeout(() => {
      shownOfferRef.current = true;
      setOfferOpen(true);
    }, OFFER_CONFIG.delayMs);
    return () => clearTimeout(timeout);
  }, [location.pathname, offerOpen]);

  return (
    <>
      <MobileNav />
      <DownloadAppModal
        open={qrModalOpen}
        onClose={closeQrModal}
        qrSrc={QR_IMAGE_SRC}
        qrLink={QR_TARGET_LINK}
        iosLink={IOS_STORE_LINK}
        androidLink={ANDROID_STORE_LINK}
      />
      <OfferPopup
        open={offerOpen}
        onClose={closeOffer}
        desktopSrc={OFFER_CONFIG.desktopSrc}
        mobileSrc={OFFER_CONFIG.mobileSrc}
        alt={OFFER_CONFIG.alt}
        ctaLink={OFFER_CONFIG.ctaLink}
      />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/accounts" element={<Accounts />} />
        <Route path="/cards" element={<Cards />} />
        <Route path="/scallop-pay" element={<ScallopPay />} />
        <Route path="/scallop-plus" element={<ScallopPlus />} />
        <Route path="/ambassador" element={<Ambassador />} />
        <Route path="/helpcenter" element={<Helpdesk />} />
        <Route path="/support" element={<Support />} />
        <Route path="/en-in" element={<EnIn />} />
        <Route path="/old-home" element={<OldHome />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/privacypolicy" element={<PrivacyPolicy />} />
        <Route path="/termsandconditions" element={<TermsAndConditions />} />
        <Route path="/refundpolicy" element={<RefundPolicy />} />
        <Route
          path="/marketingtermsandconditions"
          element={<MarketingTermsAndConditions />}
        />
      </Routes>
    </>
  );
}

export default App;
